<script lang="ts" setup>
import {RouterLink, useRouter} from 'vue-router'
import Button from 'primevue/button'
import RollsForm from '@/components/features/rolls/RollsForm.vue'
import {useRollForm} from '@/composables/useRollForm'
import {Icon} from '@iconify/vue'
import StatusTag from '@/components/table/StatusTag.vue'
import 'tippy.js/themes/light.css'
import {getRollState} from "@/constants/rolls"
import {updateRoll} from '@/api/rolls'
import type { ErrorResponse } from '@/types/error-types'

const router = useRouter()
const { values, loading, handleSubmit, setErrors, showToast, formType, batch, meta } = useRollForm('edit')
const rollId = router.currentRoute.value.params.id as string

const submit = handleSubmit(async () => {
  loading.value = true

  await updateRoll(values, rollId)
      .then(() => {
        loading.value = false
        showToast('success', 'Roll Updated!', 'You have successfully updated the roll.')
        router.push('/rolls')
      })
      .catch((error: { response?: { data: ErrorResponse } }) => {
        loading.value = false
        const errorMessages = error.response?.data?.errors?.map((err) => err.description).join('\n') || 'Unable to update roll.'
        showToast('error', 'An error occurred!', errorMessages)
      })
})
</script>

<template>
<div class="bg-white rounded-xl p-10">
    <div class="flex justify-between items-center mb-3">
      <RouterLink to="/rolls" class="flex items-center gap-x-2 group hover:text-primary-default">
        <Icon
          icon="material-symbols:arrow-back-rounded"
          class="text-3xl group-hover:translate-x-2 transition-transform"
        />
        <span>Back</span>
      </RouterLink>

      <h1 class="font-semibold text-2xl">Edit Roll</h1>

      <Button
        @click="submit"
        label="Save this Roll"
        class="min-w-48"
        :disabled="!meta.dirty || loading"
        :loading="loading"
      >
        <template #icon>
          <Icon icon="material-symbols:save" class="text-xl ml-3 order-1" />
        </template>
      </Button>
    </div>

    <div class="flex items-start gap-3 mb-4 flex-col">
      <div class="flex items-center gap-3">
        <div
          class="bg-opacity-25 py-3 px-5 pe-20 rounded-lg bg-primary-300 border border-primary-500 text-primary-500"
        >
          Roll Number: {{ values?.rollNumber }}
        </div>
        <StatusTag :status="getRollState(values?.rollState)" />
      </div>
    </div>
    <RollsForm
     :type="formType"
     :values="values"
     :onSubmit="submit"
     :setErrors="setErrors" />

</div>
</template>